import React, { useState } from 'react';
import { Button, Modal, Table, Tag, Alert, Spin, Statistic, Row, Col, Popconfirm } from 'antd';
import { SyncOutlined, CheckCircleOutlined } from '@ant-design/icons';
import axiosInstance from '../../variables/api/axiosInstance';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

/**
 * Bouton « Recalculer les statuts » des sinistres repris.
 *
 * Déroulé :
 *  1. clic → simulation (dryRun=true) : n'écrit rien, affiche ce qui SERAIT fait ;
 *  2. l'utilisateur lit les chiffres ;
 *  3. « Appliquer » → dryRun=false : écrit les statuts pour de vrai.
 *
 * Aucune manipulation de jeton : l'utilisateur est déjà authentifié dans l'appli.
 */
export default function RecalculStatutsButton() {
    const { t } = useTranslation('reprise');
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [applying, setApplying] = useState(false);
    const [rapport, setRapport] = useState(null);
    const [applique, setApplique] = useState(false);

    const lancer = async (dryRun) => {
        const setBusy = dryRun ? setLoading : setApplying;
        setBusy(true);
        try {
            const res = await axiosInstance.post(
                `/v1/reprise/recalcul-statuts?dryRun=${dryRun}`);
            const d = res.data?.data;
            setRapport(d);
            if (!dryRun) {
                setApplique(true);
                toast.success(t('recalcul.succes'));
            }
        } catch (err) {
            toast.error(err?.response?.data?.message || t('recalcul.erreur'));
        } finally {
            setBusy(false);
        }
    };

    const ouvrir = () => {
        setRapport(null);
        setApplique(false);
        setOpen(true);
        lancer(true); // simulation immédiate à l'ouverture
    };

    const transitionsRows = rapport
        ? Object.entries(rapport.transitions || {}).map(([k, v], i) => ({ key: i, transition: k, nb: v }))
        : [];

    return (
        <>
            <Button
                icon={<SyncOutlined />}
                style={{ borderColor: '#1a6b3c', color: '#1a6b3c', borderRadius: 8 }}
                onClick={ouvrir}
            >
                {t('recalcul.bouton')}
            </Button>

            <Modal
                title={t('recalcul.titre')}
                open={open}
                onCancel={() => setOpen(false)}
                width={620}
                footer={[
                    <Button key="fermer" onClick={() => setOpen(false)} style={{ borderRadius: 8 }}>
                        {t('commun.fermer')}
                    </Button>,
                    applique ? null : (
                        <Popconfirm
                            key="appliquer"
                            title={t('recalcul.confirmerTitre')}
                            description={t('recalcul.confirmerDescription')}
                            okText={t('recalcul.ouiAppliquer')}
                            cancelText={t('commun.annuler')}
                            onConfirm={() => lancer(false)}
                            disabled={loading || !rapport}
                        >
                            <Button
                                type="primary"
                                loading={applying}
                                disabled={loading || !rapport}
                                icon={<CheckCircleOutlined />}
                                style={{ background: '#1a6b3c', borderColor: '#1a6b3c', borderRadius: 8 }}
                            >
                                {t('commun.appliquer')}
                            </Button>
                        </Popconfirm>
                    ),
                ]}
            >
                {loading ? (
                    <div style={{ textAlign: 'center', padding: '32px 0' }}>
                        <Spin />
                        <div style={{ marginTop: 12, color: '#94a3b8' }}>{t('recalcul.simulationEnCours')}</div>
                    </div>
                ) : rapport ? (
                    <>
                        <Alert
                            type={applique ? 'success' : 'info'}
                            showIcon
                            style={{ marginBottom: 16, borderRadius: 8 }}
                            message={applique
                                ? t('recalcul.applique')
                                : t('recalcul.simulation')}
                        />
                        <Row gutter={16} style={{ marginBottom: 16 }}>
                            <Col span={12}>
                                <Statistic title={t('recalcul.sinistresExamines')} value={rapport.totalExamines} />
                            </Col>
                            <Col span={12}>
                                <Statistic title={t('recalcul.delaisNeutralises')} value={rapport.delaisNeutralisesBackfill} />
                            </Col>
                        </Row>
                        <Table
                            size="small"
                            pagination={false}
                            dataSource={transitionsRows}
                            columns={[
                                { title: t('recalcul.colonnes.transition'), dataIndex: 'transition', key: 't' },
                                {
                                    title: t('recalcul.colonnes.sinistres'), dataIndex: 'nb', key: 'n', align: 'right',
                                    render: (v) => <Tag color="green">{v}</Tag>,
                                },
                            ]}
                            locale={{ emptyText: t('recalcul.aucuneTransition') }}
                        />
                        {rapport.anomalies?.length > 0 && (
                            <Alert
                                type="warning"
                                showIcon
                                style={{ marginTop: 16, borderRadius: 8 }}
                                message={t('recalcul.anomalies', { count: rapport.anomalies.length })}
                            />
                        )}
                    </>
                ) : null}
            </Modal>
        </>
    );
}
